import { KeyboardEvent } from 'react';
import { SearchResultItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { MAX_TERM_LENGTH, MIN_TERM_LENGTH } from '../../../../core/queries/search.queries';
import { TextButton } from '../text-button/text-button.component';
import { SearchResults } from './search-results.component';
import { useSearchContentStyles } from './search-content.styles';

export interface SearchContentProps {
  term: string;
  onTermChange: (term: string) => void;
  includeDeliveries: boolean;
  onIncludeDeliveriesChange: (include: boolean) => void;
  onSubmit: () => void;
  isLoading: boolean;
  hasSearched: boolean;
  /** The term the on-screen results were fetched for. Labels the results once the input is cleared on reopen. */
  searchedTerm?: string;
  results: SearchResultItemDto[];
  errorMessage?: string;
  currentProjectID?: string;
  onResultClick: (item: SearchResultItemDto) => void;
  onClose: () => void;
}

export function SearchContent(props: SearchContentProps) {
  const classes = useSearchContentStyles();
  const isTermValid = props.term.trim().length >= MIN_TERM_LENGTH;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isTermValid && !props.isLoading) {
      props.onSubmit();
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          Search
        </Typography>
        <Typography variant="body2" className={classes.subtitle}>
          projects, hubs, users, team…
        </Typography>
        <IconButton className={classes.closeButton} size="small" aria-label="close search" onClick={props.onClose}>
          <Close />
        </IconButton>
      </div>

      <div className={classes.inputArea}>
        <TextField
          autoFocus
          className={classes.input}
          placeholder="Search…"
          value={props.term}
          onChange={(event) => props.onTermChange(event.target.value)}
          onKeyDown={handleKeyDown}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
              endAdornment: props.term ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="clear search" edge="end" onClick={() => props.onTermChange('')}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            },

            htmlInput: { maxLength: MAX_TERM_LENGTH }
          }}
        />
        <TextButton
          variant="outlined"
          color="primary"
          size="small"
          className={classes.searchButton}
          disabled={!isTermValid || props.isLoading}
          isLoading={props.isLoading}
          onClick={props.onSubmit}
        >
          Search
        </TextButton>
      </div>

      <FormControlLabel
        className={classes.checkbox}
        control={
          <Checkbox
            color="primary"
            checked={props.includeDeliveries}
            onChange={(event) => props.onIncludeDeliveriesChange(event.target.checked)}
          />
        }
        label="Include deliveries in search (slower)"
      />

      <div className={classes.results}>
        {props.searchedTerm && !props.term.trim() && !props.isLoading && props.results.length > 0 && (
          <Typography variant="body2" className={classes.resultsFor}>
            Results for &ldquo;{props.searchedTerm}&rdquo;
          </Typography>
        )}
        <ResultsBody {...props} />
      </div>
    </div>
  );
}

function ResultsBody(props: SearchContentProps) {
  const classes = useSearchContentStyles();

  if (props.isLoading) {
    return (
      <div className={classes.statusArea}>
        <CircularProgress />
      </div>
    );
  }

  if (props.errorMessage) {
    return (
      <Typography variant="body2" className={classes.error}>
        {props.errorMessage}
      </Typography>
    );
  }

  if (!props.hasSearched) {
    return (
      <Typography variant="body2" className={classes.message}>
        Enter at least {MIN_TERM_LENGTH} characters and search.
      </Typography>
    );
  }

  if (props.results.length === 0) {
    return (
      <Typography variant="body2" className={classes.message}>
        No results found.
      </Typography>
    );
  }

  return (
    <SearchResults
      results={props.results}
      currentProjectID={props.currentProjectID}
      onResultClick={props.onResultClick}
    />
  );
}
